import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getCorsSafeMediaUrl } from "../../../../Helper/CommonFunctions";
import { ApiBaseUrl } from "../../../../Utils/Constants";

export default function DemousdzViewer({ usdzUrl = "" }) {
  const lastAlertMessageRef = useRef("");
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [linkedFile, setLinkedFile] = useState(null);
  const [viewerState, setViewerState] = useState({
    status: usdzUrl ? "loading" : "idle",
    errorMessage: "",
  });

  const safeUsdzUrl = useMemo(
    () => getCorsSafeMediaUrl(usdzUrl),
    [usdzUrl]
  );

  const iframeUrl = useMemo(
    () => "/demousdzapp/index.html?v=iframe-safe-20260507",
    []
  );

  const getApiOrigin = () => {
    try {
      return new URL(ApiBaseUrl, window.location.origin).origin;
    } catch {
      return "";
    }
  };

  const shouldAttachAuthHeader = (url) => {
    if (!url) {
      return false;
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const apiOrigin = getApiOrigin();

      return Boolean(apiOrigin && parsedUrl.origin === apiOrigin);
    } catch {
      return false;
    }
  };

  const getAuthToken = () =>
    localStorage.getItem("professionalToken") ||
    localStorage.getItem("clientToken") ||
    localStorage.getItem("token") ||
    "";

  const getUsdzFileName = (url) => {
    if (!url || typeof url !== "string") {
      return "model.usdz";
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const nestedUrl = parsedUrl.searchParams.get("url");
      const finalUrl = nestedUrl
        ? new URL(nestedUrl, window.location.origin)
        : parsedUrl;
      const fileName = finalUrl.pathname.split("/").pop() || "model.usdz";

      return fileName.toLowerCase().endsWith(".usdz")
        ? fileName
        : "model.usdz";
    } catch {
      return "model.usdz";
    }
  };

  const readBlobPreview = async (blob) => {
    try {
      const text = await blob.slice(0, 200).text();
      return text.replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const assertValidUsdzBlob = async (blob) => {
    if (!blob || blob.size === 0) {
      throw new Error("USDZ file is empty.");
    }

    const contentType = String(blob.type || "").toLowerCase();

    if (
      contentType.includes("text/html") ||
      contentType.includes("application/json") ||
      contentType.includes("text/plain")
    ) {
      const preview = await readBlobPreview(blob);

      throw new Error(
        preview
          ? `USDZ URL returned text instead of a 3D file: ${preview}`
          : "USDZ URL returned text instead of a 3D file."
      );
    }

    const signatureBuffer = await blob.slice(0, 4).arrayBuffer();
    const signatureBytes = Array.from(new Uint8Array(signatureBuffer));
    const zipSignature =
      signatureBytes[0] === 80 &&
      signatureBytes[1] === 75 &&
      signatureBytes[2] === 3 &&
      signatureBytes[3] === 4;

    if (!zipSignature) {
      const preview = await readBlobPreview(blob);

      throw new Error(
        preview
          ? `USDZ response is not a valid archive: ${preview}`
          : "USDZ response is not a valid archive."
      );
    }
  };

  useEffect(() => {
    const onMessage = (event) => {
      if (!event?.data || event.data.type !== "DEMO_USDZ_STATUS") {
        return;
      }

      setViewerState({
        status: event.data.status || "idle",
        errorMessage: event.data.errorMessage || "",
      });
    };

    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  useEffect(() => {
    setIframeLoaded(false);
  }, [iframeUrl]);

  useEffect(() => {
    let isCancelled = false;

    if (!safeUsdzUrl) {
      setLinkedFile(null);
      setViewerState({
        status: "idle",
        errorMessage: "",
      });
      return undefined;
    }

    const loadLinkedUsdz = async () => {
      setViewerState({
        status: "loading",
        errorMessage: "",
      });

      try {
        const candidateUrls = [...new Set([safeUsdzUrl, usdzUrl].filter(Boolean))];
        let fetchedBlob = null;
        let lastError = null;

        for (const candidateUrl of candidateUrls) {
          try {
            const token = getAuthToken();

            if (shouldAttachAuthHeader(candidateUrl) && token) {
              const candidateResponse = await axios.get(candidateUrl, {
                responseType: "blob",
                headers: {
                  Authorization: token,
                },
              });

              fetchedBlob = candidateResponse.data;
              break;
            }

            const candidateResponse = await fetch(candidateUrl, { mode: "cors" });

            if (!candidateResponse.ok) {
              throw new Error(`Could not load 3D file. Status: ${candidateResponse.status}`);
            }

            fetchedBlob = await candidateResponse.blob();
            break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!fetchedBlob) {
          if (axios.isAxiosError(lastError)) {
            const status = lastError.response?.status;
            throw new Error(
              status
                ? `Could not load 3D file. Status: ${status}`
                : "Could not load 3D file from the server."
            );
          }

          throw lastError || new Error("Could not load 3D file.");
        }

        await assertValidUsdzBlob(fetchedBlob);

        const file = new File([fetchedBlob], getUsdzFileName(usdzUrl), {
          type: fetchedBlob.type || "model/vnd.usdz+zip",
        });

        if (isCancelled) {
          return;
        }

        setLinkedFile(file);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setLinkedFile(null);
        setViewerState({
          status: "error",
          errorMessage:
            error instanceof Error
              ? error.message
              : "Could not render this USDZ file in the browser.",
        });
      }
    };

    loadLinkedUsdz();

    return () => {
      isCancelled = true;
    };
  }, [safeUsdzUrl, usdzUrl]);

  useEffect(() => {
    if (!iframeLoaded || !linkedFile) {
      return;
    }

    const iframeWindow = iframeRef.current?.contentWindow;

    if (!iframeWindow) {
      return;
    }

    iframeWindow.postMessage(
      {
        type: "DEMO_USDZ_UPLOAD",
        file: linkedFile,
      },
      "*"
    );
  }, [iframeLoaded, linkedFile]);

  useEffect(() => {
    if (viewerState.status !== "error" || !viewerState.errorMessage) {
      return;
    }

    if (lastAlertMessageRef.current === viewerState.errorMessage) {
      return;
    }

    lastAlertMessageRef.current = viewerState.errorMessage;
    window.alert(viewerState.errorMessage);
  }, [viewerState.errorMessage, viewerState.status]);

  if (!safeUsdzUrl) {
    return (
      <div className="three-d-model-wrap" style={{ width: "100%" }}>
        <div className="three-d-empty-state">
          <div className="three-d-empty-badge">3D Preview</div>
          <h4>No 3D model available</h4>
          <p>
            This job does not include a USDZ file yet. Review the 2D layout or
            measurements for this room instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="three-d-model-wrap" style={{ width: "100%" }}>
      {viewerState.status === "error" && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#991b1b",
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: "999px",
              padding: "8px 12px",
            }}
          >
            {viewerState.errorMessage || "USDZ load failed"}
          </span>
        </div>
      )}

      <div className="three-d-canvas-shell" style={{ height: "560px" }}>
        <iframe
          ref={iframeRef}
          title="Demo USDZ Viewer"
          src={iframeUrl}
          onLoad={() => setIframeLoaded(true)}
          style={{
            width: "100%",
            height: "100%",
            border: "0",
          
          }}
          allow="xr-spatial-tracking; fullscreen"
        />
      </div>
    </div>
  );
}
